import { authSchema, type AuthState } from './identity';
import {worldFetch,remoteWorld} from './world-transport';
import { authErrorMessages, registrationInput, RegistrationError } from './registration';

export class AuthClient {
  state: AuthState | null = null;
  private generation = 0;
  constructor(private changed: () => void) {}
  async refresh(): Promise<void> {
    const generation = ++this.generation;
    const response = await worldFetch('/api/auth/me', { signal: AbortSignal.timeout(6000) });
    if (!response.ok) throw new Error('Account service unavailable');
    const state = authSchema.parse(await response.json());
    if (generation !== this.generation) return;
    this.state = state; this.changed();
  }
  async post(path: string, body: unknown, extraHeaders: Record<string, string> = {}): Promise<Response> {
    const headers: Record<string, string> = { ...extraHeaders, 'Content-Type': 'application/json' };
    if (this.state?.csrf) headers['X-Offworld-CSRF'] = this.state.csrf;
    return worldFetch(path, { method: 'POST', headers, body: JSON.stringify(body), signal: AbortSignal.timeout(6000) });
  }
  async command(path: string, body: unknown): Promise<unknown> {
    const response = await this.post(path, body); const value: unknown = await response.json();
    if (!response.ok) {
      if (response.status === 401) void this.refresh().catch(() => {});
      const code = typeof value === 'object' && value !== null && 'error' in value ? String(value.error) : 'Request failed';
      throw new Error(authErrorMessages[code] ?? code.replaceAll('_', ' '));
    }
    return value;
  }
}

const el = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;
export class AuthPanel {
  private busy = false;
  constructor(private auth: AuthClient) {
    el('sign-in').addEventListener('click', () => void this.run(async () => {
      await auth.command('/api/auth/login', { username: el<HTMLInputElement>('login-username').value, password: el<HTMLInputElement>('login-password').value });
      el<HTMLInputElement>('login-password').value = ''; await auth.refresh();
    }));
    el('register').addEventListener('click', () => void this.run(async () => {
      const input = registrationInput({ username: el<HTMLInputElement>('login-username').value, password: el<HTMLInputElement>('login-password').value, displayName: el<HTMLInputElement>('login-display-name').value, invitation: el<HTMLInputElement>('login-invitation').value });
      await auth.command('/api/auth/register', input);
      el<HTMLInputElement>('login-password').value = ''; el<HTMLInputElement>('login-invitation').value = ''; await auth.refresh();
    }));
    el('visit-guest').addEventListener('click', () => void this.run(async () => { await auth.command('/api/auth/guest', {}); await auth.refresh(); }));
    el('sign-out').addEventListener('click', () => void this.run(async () => { await auth.command('/api/auth/logout', {}); await auth.refresh(); }));
    el('account-settings').addEventListener('click', () => el<HTMLDialogElement>('account-dialog').showModal());
    el('save-profile').addEventListener('click', () => void this.run(async () => {
      await auth.command('/api/auth/profile', { displayName: el<HTMLInputElement>('profile-name').value.trim() }); await auth.refresh(); el('account-message').textContent = 'Profile saved. Reconnect to update presence.';
    }));
    el('change-password').addEventListener('click', () => void this.run(async () => {
      await auth.command('/api/auth/password', { currentPassword: el<HTMLInputElement>('current-password').value, newPassword: el<HTMLInputElement>('new-password').value });
      el<HTMLInputElement>('current-password').value = ''; el<HTMLInputElement>('new-password').value = ''; await auth.refresh(); el('auth-message').textContent = 'Password changed. Sign in again.';
    }));
    this.render();
  }
  private async run(work: () => Promise<void>): Promise<void> {
    if (this.busy) return; this.busy = true; el('auth-message').textContent = 'Working…'; el('account-message').textContent = '';
    try { await work(); if (el('auth-message').textContent === 'Working…') el('auth-message').textContent = ''; }
    catch (error) {
      const message = error instanceof Error ? error.message : 'Request failed'; el('auth-message').textContent = message; el('account-message').textContent = message;
      if (error instanceof RegistrationError) {
        const fields = { username: 'login-username', displayName: 'login-display-name', password: 'login-password', invitation: 'login-invitation' };
        el<HTMLInputElement>(fields[error.field]).focus();
      }
    }
    finally { this.busy = false; this.render(); }
  }
  render(): void {
    const state = this.auth.state, user = state?.user ?? null;
    el('auth-overlay').hidden = !!user;
    el('auth-forms').hidden = !state?.initialized || remoteWorld();
    el('bootstrap-help').hidden = !state || state.initialized;
    if (!state && !this.busy) el('auth-message').textContent = 'Checking account service…';
    el('account-label').textContent = user ? `${user.displayName} · ${user.role}${user.muted ? ' · chat muted' : ''}` : 'Not signed in';
    el('sign-out').hidden = !user || remoteWorld(); el('account-settings').hidden = !user || user.role === 'guest' || remoteWorld();
    el('visit-guest').hidden = !state?.allowGuests;
    if (user && document.activeElement !== el('profile-name')) el<HTMLInputElement>('profile-name').value = user.displayName;
    if (!user) { el<HTMLDialogElement>('account-dialog').close(); el<HTMLInputElement>('current-password').value = ''; el<HTMLInputElement>('new-password').value = ''; }
    for (const id of ['sign-in', 'register', 'visit-guest', 'sign-out', 'save-profile', 'change-password']) el<HTMLButtonElement>(id).disabled = this.busy;
  }
}
